import { h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import { route } from 'preact-router';
import { translateError } from '../i18n.js';
import { api } from '../state/auth.js';
import { useAuth } from '../hooks/useAuth.js';
import Card from '../components/shared/Card.jsx';
import Button from '../components/shared/Button.jsx';
import Modal from '../components/shared/Modal.jsx';
import { formatWhen, RoleBadge, STATUS_LABELS, MeetingFormModal, ROLE_LABELS } from './MeetingsView.jsx';
import { parseServerDate } from '../utils/dates.js';
import { useGoalAgenda, GoalAgendaBlock } from '../components/shared/GoalAgenda.jsx';
import AiAssist, { useAiAvailable } from '../components/shared/AiAssist.jsx';
import { aiError } from '../utils/ai.js';
import { fileLinkClick } from '../utils/openFile.js';

// ── Decisions and action points (fase 2) ─────────────────────────────────────

const smallBtn = 'border:1px solid var(--color-border);background:var(--color-bg);color:var(--color-text-muted);border-radius:6px;cursor:pointer;font-size:12px;padding:2px 8px';
const TASK_STATUS_LABELS = { open: 'Open', in_progress: 'Bezig', wacht_op_input: 'Wacht op input', afgerond: 'Afgerond', geannuleerd: 'Geannuleerd' };

function formatDate(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || '');
  if (!m) return '';
  return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Server timestamps (UTC, see utils/dates.js)
function formatStamp(value) {
  if (!value) return '';
  const d = parseServerDate(value);
  return isNaN(d) ? '' : d.toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

const PROPOSAL_LABELS = { voorgesteld: 'Voorgesteld', geaccepteerd: 'Op de agenda', afgewezen: 'Niet opgenomen' };

function Proposals({ meeting, userId, onPropose, onAccept, onReject, onWithdraw }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [open, setOpen] = useState(false);
  if (!meeting.proposals.length && !meeting.can_propose) return null;
  const submit = async () => { if (await onPropose({ title, description })) { setTitle(''); setDescription(''); setOpen(false); } };
  return (
    <div style="margin-bottom:16px">
      <Card title="Voorgestelde agendapunten">
        {meeting.proposals.length === 0 && <div style="font-size:13px;color:var(--color-text-muted)">Nog geen voorstellen.</div>}
        {meeting.proposals.map(p => (
          <div key={p.id} style="display:flex;align-items:flex-start;gap:10px;padding:8px 0;border-bottom:1px solid var(--color-border)">
            <div style="flex:1;min-width:0">
              <div style="font-size:14px;font-weight:600">{p.title}</div>
              {p.description && <div style="font-size:12px;color:var(--color-text-muted);white-space:pre-wrap">{p.description}</div>}
              <div style="font-size:11px;color:var(--color-text-muted);margin-top:2px">
                Door {p.submitted_by_name || 'onbekend'}{p.submitted_by_club ? ` (${p.submitted_by_club})` : ''} · {PROPOSAL_LABELS[p.status]}
                {p.status === 'afgewezen' && p.reason ? ` — ${p.reason}` : ''}
              </div>
            </div>
            {p.status === 'voorgesteld' && (
              <div style="display:flex;gap:4px;flex-shrink:0">
                {meeting.can_edit && !meeting.locked && <button style={smallBtn} onClick={() => onAccept(p)}>Op agenda</button>}
                {meeting.can_edit && <button style={smallBtn} onClick={() => onReject(p)}>Afwijzen</button>}
                {Number(p.submitted_by) === Number(userId) && <button style={smallBtn} onClick={() => onWithdraw(p)}>Intrekken</button>}
              </div>
            )}
          </div>
        ))}
        {meeting.can_propose && (open ? (
          <div style="padding-top:10px">
            <input class="form-input" value={title} onInput={e => setTitle(e.target.value)} placeholder="Welk punt wil je bespreken?" maxLength={160} style="margin-bottom:6px;font-size:13px" aria-label="Voorgesteld agendapunt" />
            <textarea class="form-input" rows={2} value={description} onInput={e => setDescription(e.target.value)} placeholder="Toelichting (optioneel)" style="margin-bottom:6px;font-size:13px" aria-label="Toelichting bij voorstel" />
            <AiAssist kind="meeting_agenda_description" value={description} context={{ title, meeting: meeting.title }} onUse={setDescription} />
            <div class="flex gap-2">
              <Button variant="primary" size="sm" onClick={submit} disabled={!title.trim()}>Voorstellen</Button>
              <Button variant="secondary" size="sm" onClick={() => setOpen(false)}>Annuleer</Button>
            </div>
          </div>
        ) : (
          <button style={`${smallBtn};margin-top:10px`} onClick={() => setOpen(true)}>+ Punt voorstellen</button>
        ))}
      </Card>
    </div>
  );
}

function MinutesBar({ meeting, onReady, onApprove, onReopen }) {
  const st = meeting.minutes_status;
  if (meeting.status !== 'gehouden' && !st) return null;
  let text;
  if (st?.status === 'vastgesteld') text = `Notulen vastgesteld op ${formatStamp(st.approved_at)}${st.approved_by_name ? ` door ${st.approved_by_name}` : ''}. Agenda, notulen en besluiten zijn vergrendeld.`;
  else if (st?.status === 'concept') text = `Conceptnotulen sinds ${formatStamp(st.concept_at)}. Ze worden vastgesteld in de volgende vergadering.`;
  else text = 'De notulen zijn nog niet klaargezet.';
  return (
    <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;padding:10px 12px;border-radius:8px;background:var(--color-border);font-size:13px;margin-bottom:16px">
      <span style="flex:1;min-width:200px">{st?.status === 'vastgesteld' ? '🔒 ' : '📝 '}{text}</span>
      {meeting.can_edit && !st && <Button variant="secondary" size="sm" onClick={onReady}>Notulen klaar</Button>}
      {meeting.can_edit && st?.status !== 'vastgesteld' && meeting.status === 'gehouden' && <Button variant="primary" size="sm" onClick={onApprove}>Notulen vaststellen</Button>}
      {meeting.can_manage && st?.status === 'vastgesteld' && <Button variant="secondary" size="sm" onClick={onReopen}>Heropenen</Button>}
    </div>
  );
}

export function DecisionBadge({ number }) {
  return <span style="font-size:11px;font-weight:700;padding:2px 7px;border-radius:6px;background:var(--color-primary);color:#fff;white-space:nowrap;font-variant-numeric:tabular-nums">{number}</span>;
}

function DecisionRow({ decision, canEdit, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(decision.text);
  const [club, setClub] = useState(decision.visibility === 'club');
  const save = async () => { if (await onUpdate(decision, { text, visibility: club ? 'club' : 'gremium' })) setEditing(false); };
  if (editing) {
    return (
      <div style="padding:8px 10px;border:1px solid var(--color-border);border-radius:8px;margin-top:6px">
        <textarea class="form-input" rows={2} value={text} onInput={e => setText(e.target.value)} style="font-size:13px;margin-bottom:6px" aria-label="Tekst van het besluit" />
        <AiAssist kind="meeting_decision" value={text} onUse={setText} />
        <label style="display:flex;align-items:center;gap:6px;font-size:12px;margin-bottom:8px"><input type="checkbox" checked={club} onChange={e => setClub(e.target.checked)} /> Clubbreed zichtbaar</label>
        <div class="flex gap-2">
          <Button variant="primary" size="sm" onClick={save} disabled={!text.trim()}>Opslaan</Button>
          <Button variant="secondary" size="sm" onClick={() => { setEditing(false); setText(decision.text); setClub(decision.visibility === 'club'); }}>Annuleer</Button>
        </div>
      </div>
    );
  }
  return (
    <div style="display:flex;align-items:flex-start;gap:8px;padding:6px 0">
      <span style="font-size:13px">⚖️</span>
      <DecisionBadge number={decision.number} />
      <span style="flex:1;font-size:13px;white-space:pre-wrap">{decision.text}</span>
      {decision.visibility === 'club' && <span style="font-size:11px;color:var(--color-text-muted);white-space:nowrap">Clubbreed</span>}
      {canEdit && (
        <span style="display:flex;gap:4px;flex-shrink:0">
          <button style={smallBtn} onClick={() => setEditing(true)} aria-label={`Besluit ${decision.number} bewerken`}>Bewerken</button>
          <button style={smallBtn} onClick={() => onDelete(decision)} aria-label={`Besluit ${decision.number} intrekken`}>×</button>
        </span>
      )}
    </div>
  );
}

function ActionRow({ action, canEdit, canOpenTask, showMeeting, onStatus, onDelete }) {
  const done = action.status === 'afgerond';
  const titleStyle = `flex:1;min-width:0;color:var(--color-text);${done ? 'text-decoration:line-through;color:var(--color-text-muted)' : ''}`;
  return (
    <div style="display:flex;align-items:center;gap:8px;padding:6px 0;font-size:13px">
      {canEdit
        ? <input type="checkbox" checked={done} onChange={() => onStatus(action, done ? 'open' : 'afgerond')} aria-label={`${action.title} afgerond`} />
        : <span>{done ? '☑' : '☐'}</span>}
      {canOpenTask
        ? <a href={`/tasks/${action.task_id}`} onClick={e => { e.preventDefault(); route(`/tasks/${action.task_id}`); }} style={titleStyle}>{action.title}</a>
        : <span style={titleStyle}>{action.title}</span>}
      <span style="color:var(--color-text-muted);white-space:nowrap">
        {action.owner_name || '—'}{action.deadline ? ` · ${formatDate(action.deadline)}` : ''}
        {showMeeting ? ` · uit ${action.meeting_title}` : ''}
        {!done && action.status !== 'open' ? ` · ${TASK_STATUS_LABELS[action.status] || action.status}` : ''}
      </span>
      {canEdit && onDelete && <button style={smallBtn} onClick={() => onDelete(action)} aria-label={`Actiepunt ${action.title} verwijderen`}>×</button>}
    </div>
  );
}

function AddDecisionForm({ item, initialText = '', onSave, onCancel }) {
  const [text, setText] = useState(initialText);
  const [club, setClub] = useState(false);
  return (
    <div style="padding:8px 10px;border:1px dashed var(--color-border);border-radius:8px;margin-top:6px">
      <textarea class="form-input" rows={2} value={text} onInput={e => setText(e.target.value)} placeholder="Wat is er besloten?" style="font-size:13px;margin-bottom:6px" aria-label="Nieuw besluit" />
      <AiAssist kind="meeting_decision" value={text} context={{ item }} onUse={setText} />
      <label style="display:flex;align-items:center;gap:6px;font-size:12px;margin-bottom:8px"><input type="checkbox" checked={club} onChange={e => setClub(e.target.checked)} /> Clubbreed zichtbaar (anders alleen dit gremium)</label>
      <div class="flex gap-2">
        <Button variant="primary" size="sm" onClick={() => onSave({ text, visibility: club ? 'club' : 'gremium' })} disabled={!text.trim()}>Besluit vastleggen</Button>
        <Button variant="secondary" size="sm" onClick={onCancel}>Annuleer</Button>
      </div>
    </div>
  );
}

function AddActionForm({ initialTitle = '', people, peopleError, onRetry, onSave, onCancel }) {
  const [title, setTitle] = useState(initialTitle);
  const [ownerId, setOwnerId] = useState('');
  const [deadline, setDeadline] = useState('');
  return (
    <div style="padding:8px 10px;border:1px dashed var(--color-border);border-radius:8px;margin-top:6px">
      <input class="form-input" value={title} onInput={e => setTitle(e.target.value)} placeholder="Wat moet er gebeuren?" maxLength={160} style="font-size:13px;margin-bottom:6px" aria-label="Nieuw actiepunt" />
      <div style="display:grid;grid-template-columns:2fr 1fr;gap:6px;margin-bottom:8px">
        <select class="form-input" value={ownerId} onChange={e => setOwnerId(e.target.value)} aria-label="Eigenaar" style="font-size:13px">
          <option value="">{people ? 'Wie pakt het op?' : 'Laden…'}</option>
          {(people || []).map(p => <option key={p.id} value={String(p.id)}>{p.display_name}</option>)}
        </select>
        <input class="form-input" type="date" value={deadline} onInput={e => setDeadline(e.target.value)} aria-label="Deadline" style="font-size:13px" />
      </div>
      <div class="flex gap-2">
        <Button variant="primary" size="sm" onClick={() => onSave({ title, owner_id: Number(ownerId), deadline: deadline || null })} disabled={!title.trim() || !ownerId}>Actiepunt aanmaken</Button>
        <Button variant="secondary" size="sm" onClick={onCancel}>Annuleer</Button>
      </div>
      {peopleError && <div style="font-size:12px;color:var(--color-danger,#dc2626);margin-top:6px">Leden laden mislukt. <button style={smallBtn} onClick={onRetry}>Opnieuw</button></div>}
      <div style="font-size:11px;color:var(--color-text-muted);margin-top:6px">Wordt een taak in "Mijn taken" van de eigenaar, die een melding krijgt.</div>
    </div>
  );
}

// ✨ From the (saved) notulen of one item: proposed decisions and action points. Each one
// opens the normal form, filled in; nothing is recorded until it is saved there.
function MinutesPoints({ item, onDecision, onAction }) {
  const available = useAiAvailable();
  const [busy, setBusy] = useState(false);
  const [points, setPoints] = useState(null);
  const [error, setError] = useState(null);
  if (!available) return null;
  const ask = async () => {
    setBusy(true); setError(null);
    try {
      setPoints(await api('/api/ai-help/structure', { method: 'POST', body: JSON.stringify({ kind: 'minutes_points', text: item.minutes, context: { item: item.title } }) }));
    } catch (err) { setError(aiError(err)); }
    setBusy(false);
  };
  const row = 'display:flex;align-items:flex-start;gap:8px;padding:4px 0;font-size:13px';
  return (
    <div style="margin-top:6px">
      {!points && <button style={smallBtn} onClick={ask} disabled={busy}>{busy ? '✨ Bezig…' : '✨ Besluiten en actiepunten uit de notulen'}</button>}
      {error && <div style="font-size:12px;color:var(--color-danger,#dc2626);margin-top:4px">{error}</div>}
      {points && (
        <div style="border:1px dashed var(--color-border);border-radius:8px;padding:8px 10px;margin-top:4px;background:var(--color-bg-secondary)">
          <div style="font-size:12px;color:var(--color-text-muted);margin-bottom:4px">Voorstel van de AI — kies wat je wilt vastleggen en controleer het in het formulier</div>
          {points.decisions.length === 0 && points.actions.length === 0 && <div style={row}>Geen besluiten of actiepunten gevonden in deze notulen.</div>}
          {points.decisions.map((d, i) => (
            <div key={`d-${i}`} style={row}><span>⚖️</span><span style="flex:1;min-width:0;white-space:pre-wrap">{d}</span><button style={smallBtn} onClick={() => onDecision(d)}>Als besluit…</button></div>
          ))}
          {points.actions.map((a, i) => (
            <div key={`a-${i}`} style={row}><span>☐</span><span style="flex:1;min-width:0">{a}</span>{onAction && <button style={smallBtn} onClick={() => onAction(a)}>Als actiepunt…</button>}</div>
          ))}
          <button style={`${smallBtn};margin-top:4px`} onClick={() => setPoints(null)}>Sluiten</button>
        </div>
      )}
    </div>
  );
}

// One agenda item: title, description and the minutes. Chair and secretary edit;
// everyone else reads. `children` holds the item's decisions and action points.
function AgendaItem({ item, index, count, canEdit, onSaveMinutes, onMove, onDelete, children }) {
  const [minutes, setMinutes] = useState(item.minutes || '');
  const [state, setState] = useState('idle'); // idle | saving | saved | error
  // Follow the saved notulen, but keep what the user has in the field and didn't save yet
  // (e.g. a ✨ proposal used while an earlier version was being saved)
  const saved = useRef({ id: item.id, minutes: item.minutes || '' });
  useEffect(() => {
    const next = item.minutes || '';
    setMinutes(m => (saved.current.id !== item.id || m === saved.current.minutes ? next : m));
    saved.current = { id: item.id, minutes: next };
  }, [item.id, item.minutes]);

  const dirty = minutes !== (item.minutes || '');
  const save = async () => {
    if (!dirty) return;
    setState('saving');
    try { await onSaveMinutes(item, minutes); setState('saved'); }
    catch { setState('error'); }
  };

  const small = 'border:1px solid var(--color-border);background:var(--color-bg);color:var(--color-text-muted);border-radius:6px;cursor:pointer;font-size:12px;padding:2px 7px';
  return (
    <div style="padding:14px 0;border-bottom:1px solid var(--color-border)">
      <div style="display:flex;align-items:flex-start;gap:10px">
        <span style="font-weight:700;color:var(--color-text-muted);min-width:22px">{index + 1}.</span>
        <div style="flex:1;min-width:0">
          <div style="font-weight:600;font-size:14px">{item.title}</div>
          {item.description && <div style="font-size:12px;color:var(--color-text-muted);margin-top:2px;white-space:pre-wrap">{item.description}</div>}
        </div>
        {canEdit && (
          <div style="display:flex;gap:4px;flex-shrink:0">
            <button style={small} disabled={index === 0} onClick={() => onMove(index, -1)} title="Omhoog" aria-label="Omhoog">↑</button>
            <button style={small} disabled={index === count - 1} onClick={() => onMove(index, 1)} title="Omlaag" aria-label="Omlaag">↓</button>
            {item.kind === 'regulier' && <button style={small} onClick={() => onDelete(item)} title="Punt verwijderen" aria-label="Punt verwijderen">×</button>}
          </div>
        )}
      </div>
      <div style="margin:8px 0 0 32px">
        {canEdit ? (
          <>
            <textarea class="form-input" rows={minutes ? 3 : 2} value={minutes} placeholder="Notulen bij dit punt…"
              onInput={e => { setMinutes(e.target.value); setState('idle'); }} onBlur={save}
              style="resize:vertical;min-height:52px;font-size:13px" aria-label={`Notulen bij ${item.title}`} />
            <AiAssist kind="meeting_minutes" value={minutes} context={{ item: item.title }} onUse={text => { setMinutes(text); setState('idle'); }} />
            <div style="display:flex;align-items:center;gap:8px;margin-top:4px;min-height:22px">
              {dirty && <Button variant="secondary" size="sm" onClick={save}>Opslaan</Button>}
              <span style="font-size:11px;color:var(--color-text-muted)">
                {state === 'saving' ? 'Opslaan…' : state === 'saved' && !dirty ? 'Opgeslagen' : state === 'error' ? 'Opslaan mislukt, probeer opnieuw' : ''}
              </span>
            </div>
          </>
        ) : (
          item.minutes
            ? <div style="font-size:13px;white-space:pre-wrap;background:var(--color-bg);border-radius:8px;padding:8px 10px">{item.minutes}</div>
            : <div style="font-size:12px;color:var(--color-text-muted)">Nog geen notulen.</div>
        )}
        {children}
      </div>
    </div>
  );
}

// What happened to the PDF of approved minutes (routes/meetings.js minutes/approve)
function archiveNotice(archive) {
  if (archive?.status === 'saved') return `Notulen vastgesteld. De PDF staat in Documenten, in de map "${archive.folderName}"${archive.version > 1 ? ` (versie ${archive.version})` : ''}.`;
  if (archive?.status === 'no_commission') return 'Notulen vastgesteld. Dit gremium hangt niet aan een commissie, dus de PDF staat niet in Documenten. Download hem met de knop PDF.';
  return 'Notulen vastgesteld, maar de PDF kon niet in Documenten worden gezet. Download hem met de knop PDF.';
}

// ── Attendance ────────────────────────────────────────────────────────────────

const ATTENDANCE_LABELS = { aanwezig: 'Aanwezig', afgemeld: 'Afgemeld', afwezig: 'Afwezig' };
const MAX_GUESTS = 30; // services/meeting-service.js MAX_GUESTS

function attendanceSummary(s) {
  const parts = [`${s.aanwezig} aanwezig`];
  if (s.afgemeld) parts.push(`${s.afgemeld} afgemeld`);
  if (s.afwezig) parts.push(`${s.afwezig} afwezig`);
  if (s.gasten) parts.push(`${s.gasten} ${s.gasten === 1 ? 'gast' : 'gasten'}`);
  return parts.join(', ');
}

const names = list => list.map(m => m.name + (m.from_agenda ? ' (via agenda)' : '')).join(', ');

function Attendance({ attendance, canEdit, onSave }) {
  const [editing, setEditing] = useState(false);
  const [statuses, setStatuses] = useState({});
  const [guests, setGuests] = useState([]);
  const [guestName, setGuestName] = useState('');
  const [saving, setSaving] = useState(false);
  const members = attendance.members.filter(m => m.user_id != null);

  const start = () => {
    setStatuses(Object.fromEntries(members.map(m => [m.user_id, m.status])));
    setGuests(attendance.guests.map(g => g.name));
    setGuestName('');
    setEditing(true);
  };
  // Clicking the chosen status again clears it; a decline from the agenda can only be
  // changed to another status (cleared, it would come back from the agenda)
  const fromAgenda = new Set(members.filter(m => m.from_agenda).map(m => m.user_id));
  const pick = (userId, status) => setStatuses(st => {
    if (st[userId] !== status) return { ...st, [userId]: status };
    return status === 'afgemeld' && fromAgenda.has(userId) ? st : { ...st, [userId]: null };
  });
  const allPresent = () => setStatuses(st => Object.fromEntries(members.map(m => [m.user_id, st[m.user_id] || 'aanwezig'])));
  const guestsFull = guests.length >= MAX_GUESTS;
  const addGuest = () => {
    const name = guestName.trim();
    if (!name || guestsFull) return;
    setGuests(g => [...g, name]);
    setGuestName('');
  };
  const save = async () => {
    setSaving(true);
    const pending = guestName.trim() && !guestsFull ? [...guests, guestName.trim()] : guests;
    const ok = await onSave({ members: members.map(m => ({ user_id: m.user_id, status: statuses[m.user_id] ?? null })), guests: pending });
    setSaving(false);
    if (ok) setEditing(false);
  };

  const groups = ['aanwezig', 'afgemeld', 'afwezig'].map(st => [st, attendance.members.filter(m => m.status === st)]);
  // Who said in the agenda (or, for a club's representative, in their own app) they come
  const coming = attendance.members.filter(m => !m.status && m.rsvp === 'aangemeld');
  const open = attendance.members.filter(m => !m.status && m.rsvp !== 'aangemeld');
  const segBtn = active => `border:1px solid ${active ? 'var(--color-primary)' : 'var(--color-border)'};background:${active ? 'var(--color-primary)' : 'var(--color-surface)'};color:${active ? '#fff' : 'var(--color-text-muted)'};border-radius:6px;cursor:pointer;font-size:12px;padding:3px 8px`;

  return (
    <Card title="Aanwezig" actions={canEdit && !editing && <Button variant="secondary" size="sm" onClick={start}>Invullen</Button>}>
      {!editing ? (
        <div style="font-size:13px;display:flex;flex-direction:column;gap:4px">
          <div style="color:var(--color-text-muted)">{attendanceSummary(attendance.summary)}</div>
          {groups.map(([st, list]) => list.length > 0 && <div key={st}><b>{ATTENDANCE_LABELS[st]}:</b> {names(list)}</div>)}
          {attendance.guests.length > 0 && <div><b>Gasten:</b> {attendance.guests.map(g => g.name).join(', ')}</div>}
          {coming.length > 0 && <div><b>Komt (aangemeld):</b> {names(coming)}</div>}
          {open.length > 0 && <div style="color:var(--color-text-muted)"><b>Nog niet ingevuld:</b> {names(open)}</div>}
        </div>
      ) : (
        <div style="display:flex;flex-direction:column;gap:10px;font-size:13px">
          {members.length === 0 && <div style="color:var(--color-text-muted)">Dit gremium heeft nog geen leden.</div>}
          {members.map(m => (
            <div key={m.user_id} style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">
              <span style="flex:1;min-width:140px">{m.name}{m.role && m.role !== 'lid' ? <span style="color:var(--color-text-muted)"> · {ROLE_LABELS[m.role] || m.role}</span> : null}{m.former ? <span style="color:var(--color-text-muted)"> · niet meer in gremium</span> : null}{m.rsvp === 'aangemeld' ? <span style="color:var(--color-text-muted)"> · aangemeld</span> : null}</span>
              <span style="display:flex;gap:4px" role="group" aria-label={`Aanwezigheid ${m.name}`}>
                {Object.entries(ATTENDANCE_LABELS).map(([st, label]) => (
                  <button key={st} type="button" style={segBtn(statuses[m.user_id] === st)} aria-pressed={statuses[m.user_id] === st} onClick={() => pick(m.user_id, st)}>{label}</button>
                ))}
              </span>
            </div>
          ))}
          <div>
            <div style="font-weight:600;margin-bottom:4px">Gasten</div>
            <div style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:6px">
              {guests.map((g, i) => (
                <span key={i} style="display:flex;align-items:center;gap:4px;padding:2px 8px;border-radius:12px;border:1px solid var(--color-border);font-size:12px">
                  {g}<button type="button" onClick={() => setGuests(list => list.filter((_, j) => j !== i))} aria-label={`Gast ${g} verwijderen`} style="background:none;border:none;cursor:pointer;color:var(--color-text-muted);font-size:14px;line-height:1;padding:0 2px">×</button>
                </span>
              ))}
            </div>
            <div style="display:flex;gap:6px">
              <input class="form-input" value={guestName} onInput={e => setGuestName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addGuest(); } }} placeholder="Naam, bijvoorbeeld Piet de Vries (KNVB)" maxLength={120} style="flex:1;min-width:0;font-size:13px" aria-label="Naam gast" />
              <button type="button" style={smallBtn} onClick={addGuest} disabled={!guestName.trim() || guestsFull}>Toevoegen</button>
            </div>
            {guestsFull && <div style="font-size:12px;color:var(--color-text-muted);margin-top:4px">Maximaal {MAX_GUESTS} gasten.</div>}
          </div>
          <div style="display:flex;gap:8px;flex-wrap:wrap">
            <Button variant="primary" size="sm" onClick={save} disabled={saving}>{saving ? 'Opslaan…' : 'Opslaan'}</Button>
            <Button variant="secondary" size="sm" onClick={() => setEditing(false)} disabled={saving}>Annuleren</Button>
            {members.length > 0 && <button type="button" style={smallBtn} onClick={allPresent}>Rest op aanwezig</button>}
          </div>
        </div>
      )}
    </Card>
  );
}

export default function MeetingDetailView({ id }) {
  const auth = useAuth();
  const canOpenTask = auth.hasPermission('tasks', 'read');
  const [meeting, setMeeting] = useState(null);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [openForm, setOpenForm] = useState(null); // { itemId, kind: 'decision' | 'action', initial }
  const [people, setPeople] = useState(null);
  const [peopleError, setPeopleError] = useState(false);
  const [notice, setNotice] = useState(null);
  const [goalAgenda, reloadGoalAgenda] = useGoalAgenda(id);

  const load = () => api(`/api/meetings/${id}`).then(setMeeting).catch(e => setError(translateError(e.code, e.detail)));
  // Why: following the previous-meeting link reuses this component with a new id
  useEffect(() => { setNotice(null); setActionError(null); setOpenForm(null); load(); }, [id]);

  const setAgenda = agenda => setMeeting(m => ({ ...m, agenda }));
  const run = async (fn) => {
    setActionError(null);
    try { await fn(); } catch (e) { setActionError(translateError(e.code, e.detail)); }
  };

  const saveMinutes = async (item, minutes) => {
    const updated = await api(`/api/meetings/${id}/agenda/${item.id}`, { method: 'PUT', body: JSON.stringify({ minutes }) });
    setMeeting(m => ({ ...m, agenda: m.agenda.map(a => a.id === updated.id ? updated : a) }));
  };
  const move = (index, delta) => run(async () => {
    const ids = meeting.agenda.map(a => a.id);
    [ids[index], ids[index + delta]] = [ids[index + delta], ids[index]];
    setAgenda(await api(`/api/meetings/${id}/agenda/order`, { method: 'PUT', body: JSON.stringify({ ids }) }));
  });
  const remove = (item) => {
    if (!confirm(`Agendapunt "${item.title}" verwijderen?`)) return;
    run(async () => { setAgenda(await api(`/api/meetings/${id}/agenda/${item.id}`, { method: 'DELETE' })); await reloadGoalAgenda(); });
  };
  const addItem = () => run(async () => {
    if (!newTitle.trim()) return;
    setAgenda(await api(`/api/meetings/${id}/agenda`, { method: 'POST', body: JSON.stringify({ title: newTitle.trim(), description: newDescription.trim() }) }));
    setNewTitle(''); setNewDescription(''); setAdding(false);
  });
  const setStatus = (status) => run(async () => {
    const updated = await api(`/api/meetings/${id}`, { method: 'PUT', body: JSON.stringify({ status }) });
    setMeeting(m => ({ ...m, ...updated }));
    setConfirmCancel(false);
  });
  const refresh = () => api(`/api/meetings/${id}`).then(setMeeting);
  const loadPeople = () => {
    setPeopleError(false);
    api(`/api/meetings/${id}/people`).then(setPeople).catch(() => setPeopleError(true));
  };
  const showForm = (itemId, kind, initial = '') => {
    setOpenForm({ itemId, kind, initial });
    if (kind === 'action' && !people) loadPeople();
  };
  const addDecision = (itemId, form) => run(async () => {
    await api(`/api/meetings/${id}/decisions`, { method: 'POST', body: JSON.stringify({ ...form, agenda_item_id: itemId }) });
    setOpenForm(null); await refresh();
  });
  // Returns whether it worked, so the editor stays open on failure
  const updateDecision = async (decision, form) => {
    let ok = false;
    await run(async () => {
      await api(`/api/meetings/${id}/decisions/${decision.id}`, { method: 'PUT', body: JSON.stringify(form) });
      ok = true;
      await refresh();
    });
    return ok;
  };
  const deleteDecision = (decision) => {
    if (!confirm(`Besluit ${decision.number} intrekken? Het nummer wordt niet opnieuw gebruikt.`)) return;
    run(async () => { await api(`/api/meetings/${id}/decisions/${decision.id}`, { method: 'DELETE' }); await refresh(); });
  };
  const addAction = (itemId, form) => run(async () => {
    await api(`/api/meetings/${id}/actions`, { method: 'POST', body: JSON.stringify({ ...form, agenda_item_id: itemId }) });
    setOpenForm(null); await refresh();
  });
  const setActionStatus = (action, status) => run(async () => {
    await api(`/api/meetings/${id}/actions/${action.id}`, { method: 'PUT', body: JSON.stringify({ status }) });
    await refresh();
  });
  const deleteAction = (action) => {
    if (!confirm(`Actiepunt "${action.title}" en de bijbehorende taak verwijderen?`)) return;
    run(async () => { await api(`/api/meetings/${id}/actions/${action.id}`, { method: 'DELETE' }); await refresh(); });
  };

  // ── fase 3 ──
  const propose = async (form) => {
    let ok = false;
    await run(async () => { await api(`/api/meetings/${id}/proposals`, { method: 'POST', body: JSON.stringify(form) }); ok = true; await refresh(); });
    return ok;
  };
  const acceptProposal = (p) => run(async () => { await api(`/api/meetings/${id}/proposals/${p.id}/accept`, { method: 'POST' }); await refresh(); });
  const rejectProposal = (p) => {
    const reason = prompt(`Waarom wordt "${p.title}" niet opgenomen? (optioneel)`);
    if (reason === null) return;
    run(async () => { await api(`/api/meetings/${id}/proposals/${p.id}/reject`, { method: 'POST', body: JSON.stringify({ reason }) }); await refresh(); });
  };
  const withdrawProposal = (p) => {
    if (!confirm(`Voorstel "${p.title}" intrekken?`)) return;
    run(async () => { await api(`/api/meetings/${id}/proposals/${p.id}`, { method: 'DELETE' }); await refresh(); });
  };
  const sendAgenda = () => {
    if (!confirm('Alle leden een melding sturen dat de agenda klaarstaat?')) return;
    run(async () => {
      const r = await api(`/api/meetings/${id}/agenda/send`, { method: 'POST' });
      setNotice(`Agenda verstuurd naar ${r.sent} ${r.sent === 1 ? 'lid' : 'leden'}.`);
      await refresh();
    });
  };
  const minutesReady = () => {
    if (!confirm('Notulen als concept klaarzetten? Alle leden krijgen een melding.')) return;
    run(async () => { await api(`/api/meetings/${id}/minutes/ready`, { method: 'POST' }); await refresh(); });
  };
  const approveMinutes = (meetingId, inMeetingId) => {
    if (!confirm('Notulen vaststellen? Daarna zijn agenda, notulen en besluiten van die vergadering vergrendeld.')) return;
    run(async () => {
      const result = await api(`/api/meetings/${meetingId}/minutes/approve`, { method: 'POST', body: JSON.stringify(inMeetingId ? { in_meeting_id: inMeetingId } : {}) });
      setNotice(archiveNotice(result?.archive));
      await refresh();
      await reloadGoalAgenda();
    });
  };
  const reopenMinutes = () => {
    if (!confirm('Vastgestelde notulen heropenen? Ze worden weer concept en zijn te wijzigen.')) return;
    run(async () => { await api(`/api/meetings/${id}/minutes/reopen`, { method: 'POST' }); await refresh(); await reloadGoalAgenda(); });
  };

  // Returns whether it worked, so the editor stays open on failure
  const saveAttendance = async (form) => {
    let ok = false;
    await run(async () => {
      const attendance = await api(`/api/meetings/${id}/attendance`, { method: 'PUT', body: JSON.stringify(form) });
      setMeeting(m => ({ ...m, attendance }));
      ok = true;
    });
    return ok;
  };

  const saveMeeting = async (form) => {
    const updated = await api(`/api/meetings/${id}`, { method: 'PUT', body: JSON.stringify(form) });
    setMeeting(m => ({ ...m, ...updated }));
    setEditing(false);
  };

  const back = meeting ? `/meetings/bodies/${meeting.body.id}` : '/meetings';
  const backLink = (
    <a href={back} onClick={e => { e.preventDefault(); route(back); }} style="font-size:13px;color:var(--color-text-muted);text-decoration:none">
      ‹ {meeting ? meeting.body.name : 'Vergaderingen'}
    </a>
  );
  if (error) return <div>{backLink}<div class="alert alert-error mt-4">{error}</div></div>;
  if (!meeting) return <div style="color:var(--color-text-muted)">Laden…</div>;

  const cancelled = meeting.status === 'geannuleerd';
  // Why: approved minutes lock the content; ticking off action points stays possible
  const canEditContent = meeting.can_edit && !meeting.locked;
  return (
    <div>
      {backLink}
      <div class="page-header" style="margin-top:6px;align-items:flex-start">
        <div style="min-width:0">
          <h1 class="page-title" style={cancelled ? 'text-decoration:line-through' : ''}>{meeting.title}</h1>
          <div style="font-size:13px;color:var(--color-text-muted);display:flex;flex-wrap:wrap;gap:6px 14px;align-items:center">
            <span>📅 {formatWhen(meeting.starts_at, meeting.ends_at)}</span>
            {meeting.location && <span>📍 {meeting.location}</span>}
            <span>{STATUS_LABELS[meeting.status]}</span>
            <RoleBadge role={meeting.my_role} />
          </div>
        </div>
        {!cancelled && (
          <a class="btn btn-secondary btn-sm" href={`/api/meetings/${meeting.id}/minutes.pdf`} download data-native onClick={fileLinkClick} title={meeting.minutes_status?.status === 'vastgesteld' ? 'Notulen als PDF' : 'Notulen als PDF (concept)'} style="white-space:nowrap;text-decoration:none">📄 PDF</a>
        )}
        {meeting.can_edit && !meeting.locked && (
          <div style="display:flex;gap:8px;flex-wrap:wrap">
            {meeting.status === 'gepland' && <Button variant="secondary" size="sm" onClick={sendAgenda}>Agenda versturen</Button>}
            <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>Bewerken</Button>
            {meeting.status === 'gepland' && <Button variant="secondary" size="sm" onClick={() => setStatus('gehouden')}>Markeer als gehouden</Button>}
            {meeting.status === 'gepland' && <Button variant="secondary" size="sm" onClick={() => setConfirmCancel(true)}>Annuleren</Button>}
            {meeting.status !== 'gepland' && <Button variant="secondary" size="sm" onClick={() => setStatus('gepland')}>Terug naar gepland</Button>}
          </div>
        )}
      </div>
      {actionError && <div class="alert alert-error mb-4">{actionError}</div>}
      {notice && <div class="alert mb-4" style="background:var(--color-border);border-radius:8px;padding:8px 12px;font-size:13px">{notice}</div>}
      {meeting.agenda_sent_at && meeting.status === 'gepland' && (
        <div style="font-size:12px;color:var(--color-text-muted);margin:-8px 0 12px">Agenda laatst verstuurd op {formatStamp(meeting.agenda_sent_at)}</div>
      )}
      <MinutesBar meeting={meeting} onReady={minutesReady} onApprove={() => approveMinutes(meeting.id)} onReopen={reopenMinutes} />
      <Proposals meeting={meeting} userId={auth.userId} onPropose={propose} onAccept={acceptProposal} onReject={rejectProposal} onWithdraw={withdrawProposal} />
      {!cancelled && meeting.attendance && <Attendance key={meeting.id} attendance={meeting.attendance} canEdit={canEditContent} onSave={saveAttendance} />}

      <Card title="Agenda en notulen">
        {meeting.agenda.map((item, i) => {
          const decisions = meeting.decisions.filter(d => d.agenda_item_id === item.id);
          const actions = meeting.actions.filter(a => a.agenda_item_id === item.id);
          const form = openForm?.itemId === item.id ? openForm.kind : null;
          return (
            <AgendaItem key={item.id} item={item} index={i} count={meeting.agenda.length} canEdit={canEditContent}
              onSaveMinutes={saveMinutes} onMove={move} onDelete={remove}>
              {item.kind === 'notulen' && (
                <div style="margin-top:8px;font-size:13px">
                  {meeting.previous_meeting ? (
                    <span style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">
                      <span>Notulen van{' '}
                        <a href={`/meetings/${meeting.previous_meeting.id}`} onClick={e => { e.preventDefault(); route(`/meetings/${meeting.previous_meeting.id}`); }}>
                          {meeting.previous_meeting.title}
                        </a>
                        {' — '}{meeting.previous_meeting.minutes_status?.status === 'vastgesteld' ? 'vastgesteld' : meeting.previous_meeting.minutes_status?.status === 'concept' ? 'concept' : 'nog niet klaargezet'}
                      </span>
                      {canEditContent && meeting.status !== 'geannuleerd' && meeting.previous_meeting.status === 'gehouden' && meeting.previous_meeting.minutes_status?.status !== 'vastgesteld' && (
                        <button style={smallBtn} onClick={() => approveMinutes(meeting.previous_meeting.id, meeting.id)}>Vaststellen</button>
                      )}
                    </span>
                  ) : <span style="color:var(--color-text-muted)">Geen eerdere vergadering.</span>}
                </div>
              )}
              {goalAgenda.item_ids.includes(item.id) && <GoalAgendaBlock key={`${item.id}:${meeting.minutes_status?.status || ''}`} meetingId={meeting.id} itemId={item.id} />}
              {item.kind === 'actielijst' && !meeting.complex && (
                <div style="margin-top:8px">
                  {meeting.open_actions.length === 0
                    ? <div style="font-size:12px;color:var(--color-text-muted)">Geen openstaande actiepunten uit eerdere vergaderingen.</div>
                    : meeting.open_actions.map(a => <ActionRow key={a.id} action={a} canOpenTask={canOpenTask} canEdit={meeting.can_edit} showMeeting onStatus={setActionStatus} />)}
                </div>
              )}
              {(decisions.length > 0 || actions.length > 0) && (
                <div style="margin-top:8px">
                  {decisions.map(d => <DecisionRow key={d.id} decision={d} canEdit={canEditContent} onUpdate={updateDecision} onDelete={deleteDecision} />)}
                  {actions.map(a => <ActionRow key={a.id} action={a} canOpenTask={canOpenTask} canEdit={meeting.can_edit} onStatus={setActionStatus} onDelete={canEditContent ? deleteAction : null} />)}
                </div>
              )}
              {form === 'decision' && <AddDecisionForm key={`d-${openForm.initial}`} item={item.title} initialText={openForm.initial} onSave={f => addDecision(item.id, f)} onCancel={() => setOpenForm(null)} />}
              {form === 'action' && <AddActionForm key={`a-${openForm.initial}`} initialTitle={openForm.initial} people={people} peopleError={peopleError} onRetry={loadPeople} onSave={f => addAction(item.id, f)} onCancel={() => setOpenForm(null)} />}
              {canEditContent && !form && (
                <div style="display:flex;gap:6px;margin-top:6px">
                  <button style={smallBtn} onClick={() => showForm(item.id, 'decision')}>+ Besluit</button>
                  {!meeting.complex && <button style={smallBtn} onClick={() => showForm(item.id, 'action')}>+ Actiepunt</button>}
                </div>
              )}
              {canEditContent && item.minutes && (
                <MinutesPoints key={item.minutes} item={item}
                  onDecision={text => showForm(item.id, 'decision', text)} onAction={meeting.complex ? null : title => showForm(item.id, 'action', title)} />
              )}
            </AgendaItem>
          );
        })}
        {canEditContent && (
          adding ? (
            <div style="padding-top:14px">
              <input class="form-input" value={newTitle} onInput={e => setNewTitle(e.target.value)} placeholder="Titel van het agendapunt" maxLength={160} style="margin-bottom:8px" aria-label="Titel van het agendapunt" />
              <textarea class="form-input" rows={2} value={newDescription} onInput={e => setNewDescription(e.target.value)} placeholder="Toelichting (optioneel)" style="margin-bottom:8px;font-size:13px" aria-label="Toelichting" />
              <AiAssist kind="meeting_agenda_description" value={newDescription} context={{ title: newTitle, meeting: meeting.title }} onUse={setNewDescription} />
              <div class="flex gap-2">
                <Button variant="primary" size="sm" onClick={addItem} disabled={!newTitle.trim()}>Toevoegen</Button>
                <Button variant="secondary" size="sm" onClick={() => { setAdding(false); setNewTitle(''); setNewDescription(''); }}>Annuleer</Button>
              </div>
            </div>
          ) : (
            <span style="display:flex;gap:8px;flex-wrap:wrap">
              <button onClick={() => setAdding(true)}
                style="margin-top:12px;padding:7px 12px;border:1px dashed var(--color-border);border-radius:8px;background:transparent;color:var(--color-text-muted);cursor:pointer;font-size:13px">
                + Agendapunt toevoegen
              </button>
              {goalAgenda.can_add && (
                <button onClick={() => run(async () => { await api(`/api/goals/meetings/${meeting.id}/agenda`, { method: 'POST' }); await load(); await reloadGoalAgenda(); })}
                  style="margin-top:12px;padding:7px 12px;border:1px dashed var(--color-border);border-radius:8px;background:transparent;color:var(--color-text-muted);cursor:pointer;font-size:13px">
                  + Voortgang doelen
                </button>
              )}
            </span>
          )
        )}
      </Card>

      {(() => {
        // Decisions and actions whose agenda item was removed stay visible here
        const itemIds = new Set(meeting.agenda.map(a => a.id));
        const loseD = meeting.decisions.filter(d => !itemIds.has(d.agenda_item_id));
        const loseA = meeting.actions.filter(a => !itemIds.has(a.agenda_item_id));
        if (!loseD.length && !loseA.length) return null;
        return (
          <div style="margin-top:16px">
            <Card title="Overige besluiten en actiepunten">
              {loseD.map(d => <DecisionRow key={d.id} decision={d} canEdit={canEditContent} onUpdate={updateDecision} onDelete={deleteDecision} />)}
              {loseA.map(a => <ActionRow key={a.id} action={a} canOpenTask={canOpenTask} canEdit={meeting.can_edit} onStatus={setActionStatus} onDelete={canEditContent ? deleteAction : null} />)}
            </Card>
          </div>
        );
      })()}

      {editing && <MeetingFormModal meeting={meeting} title="Vergadering bewerken" onClose={() => setEditing(false)} onSubmit={saveMeeting} />}
      {confirmCancel && (
        <Modal isOpen title="Vergadering annuleren?" onClose={() => setConfirmCancel(false)}>
          <p style="font-size:14px;margin:0 0 16px">De afspraak verdwijnt uit de kalender van de leden. Agenda en notulen blijven bewaard.</p>
          <div class="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => setConfirmCancel(false)}>Terug</Button>
            <Button variant="danger" onClick={() => setStatus('geannuleerd')}>Annuleren</Button>
          </div>
        </Modal>
      )}
    </div>
  );
}
